import { useState, useEffect } from 'react';
import { LogOut, Menu, X, Droplet, ChevronDown, ChevronRight, Settings, ChevronLeft, Globe, MessageSquarePlus, Image as ImageIcon, LayoutDashboard, BarChart3 } from 'lucide-react';
import { changelog } from '../lib/changelog';
import { BUILD_SHORT_SHA, BUILD_AT } from '../lib/buildInfo';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { msalInstance } from '../lib/msalConfig';
import { UserRole, type SigninDesign } from '../lib/auth';
import { SIGNIN_DESIGN_STORAGE_KEY } from './SignIn';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES } from '../lib/auditLog';
import { supportedLanguages, SupportedLanguage } from '../i18n/config';
import { ThemeSwitcher } from './ThemeSwitcher';
import { ConnectionStatus } from './ConnectionStatus';
import { FeedbackNotificationsBell } from './FeedbackNotificationsBell';
import { NotificationBanner } from './NotificationBanner';
import { SupportChatModal } from './SupportChatModal';
import { fetchEffectiveTheme, applyThemeToDOM, storeThemeSlug } from '../services/themeService';
import type { AppTheme } from '../types/theme';
import { DEFAULT_APP_COLORS } from '../types/theme';

type NavPage =
  | 'dashboard'
  | 'compliance-report'
  | 'inspection-completion'
  | 'watering-schedules'
  | 'start-inspection'
  | 'manage-devices'
  | 'view-inspections'
  | 'inspections-dashboard'
  | 'review-report'
  | 'job-sites'
  | 'user-management'
  | 'report-status'
  | 'job-codes'
  | 'controller-settings'
  | 'integrations'
  | 'feedback-management'
  | 'audit-log'
  | 'event-log'
  | 'gps-diagnostics'
  | 'notifications'
  | 'water-restrictions'
  | 'theme-management';

interface LayoutProps {
  children: React.ReactNode;
  userId?: string;
  userEmail?: string;
  userRole?: UserRole | null;
  /** The signed-in user's chosen theme slug (null = org default). Per-user. */
  userThemeSlug?: string | null;
  currentPage?: string;
  onNavigate?: (page: NavPage) => void;
  onOpenFeedback?: () => void;
}

export function Layout({ children, userId, userEmail, userRole, userThemeSlug, currentPage, onNavigate, onOpenFeedback }: LayoutProps) {
  const { t, i18n } = useTranslation('common');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isInspectionsOpen, setIsInspectionsOpen] = useState(true);
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isDesignMenuOpen, setIsDesignMenuOpen] = useState(false);
  const [signinDesign, setSigninDesign] = useState<SigninDesign>(() => {
    if (typeof localStorage === 'undefined') return 'random';
    const v = localStorage.getItem(SIGNIN_DESIGN_STORAGE_KEY);
    return v === 'split-panel' || v === 'photogenic' || v === 'random' ? v : 'random';
  });
  const [isChangelogOpen, setIsChangelogOpen] = useState(false);
  const [activeTheme, setActiveTheme] = useState<AppTheme | null>(null);
  const [companyName, setCompanyName] = useState('IrrigationPro');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  const colors = activeTheme?.app_colors || DEFAULT_APP_COLORS;

  useEffect(() => {
    loadActiveTheme();
    fetchCompanyInfo();

    const handleUpdate = () => {
      loadActiveTheme();
      fetchCompanyInfo();
    };

    window.addEventListener('brandSettingsUpdated', handleUpdate);
    window.addEventListener('themeUpdated', handleUpdate);
    return () => {
      window.removeEventListener('brandSettingsUpdated', handleUpdate);
      window.removeEventListener('themeUpdated', handleUpdate);
    };
  }, []);

  useEffect(() => {
    // Collapse the nav on the immersive, full-width screens so they get the
    // whole viewport. Start Inspection is an iPad-first swimlane board that
    // needs the width to show the full 7-day schedule; the user can reopen the
    // nav with the header toggle.
    if (
      currentPage === 'inspection-form' ||
      currentPage === 'review-report' ||
      currentPage === 'start-inspection'
    ) {
      setIsSidebarOpen(false);
    }
  }, [currentPage]);

  const loadActiveTheme = async () => {
    try {
      const theme = await fetchEffectiveTheme(userThemeSlug);
      if (theme) {
        setActiveTheme(theme);
        applyThemeToDOM(theme.app_colors);
        storeThemeSlug(theme.slug);
      }
    } catch (err) {
      console.error('Failed to load active theme:', err);
    }
  };

  const handleThemeChange = (theme: AppTheme) => {
    setActiveTheme(theme);
    applyThemeToDOM(theme.app_colors);
  };

  const fetchCompanyInfo = async () => {
    try {
      const data = await apiClient.get<{ company_name?: string | null; logo_url?: string | null }>('/api/brand-settings');
      setCompanyName(data.company_name || 'IrrigationPro');
      setLogoUrl(data.logo_url ?? null);
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const handleSignOut = async () => {
    await logAudit({
      action: AUDIT_ACTIONS.LOGOUT,
      entityType: AUDIT_ENTITIES.SESSION,
      entityLabel: userEmail || '',
    });
    sessionStorage.removeItem('login_logged');
    await msalInstance.logoutRedirect();
  };

  const handleLanguageChange = async (lang: SupportedLanguage) => {
    await i18n.changeLanguage(lang);
    localStorage.setItem('preferred_locale', lang);
    setIsLangMenuOpen(false);

    if (!userId) return;
    try {
      // Self-service endpoint — works for User/Manager/Admin alike. The old
      // path used /api/users/:id which is Admin-only, so non-admins silently
      // 403'd and their preference never persisted.
      await apiClient.put('/api/auth/me', { preferredLocale: lang });
    } catch (error) {
      console.error('Error saving language preference:', error);
    }
  };

  const handleSigninDesignChange = async (design: SigninDesign) => {
    setSigninDesign(design);
    setIsDesignMenuOpen(false);
    try {
      localStorage.setItem(SIGNIN_DESIGN_STORAGE_KEY, design);
    } catch {
      /* best-effort */
    }
    if (!userId) return;
    try {
      await apiClient.put('/api/auth/me', { signinDesign: design });
    } catch (error) {
      console.error('Error saving sign-in design preference:', error);
    }
  };

  const menuTextColor = colors.menu_text_color;

  return (
    <div className="h-screen flex flex-col" style={{ backgroundColor: colors.page_bg_color }}>
      <header
        className="shadow-sm flex-shrink-0 z-40"
        style={{ backgroundColor: colors.header_bg_color, borderBottom: `1px solid ${colors.border_color}` }}
      >
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 rounded-lg transition-colors"
              style={{ color: colors.header_text_color }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.hover_bg_color}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              {isSidebarOpen ? <ChevronLeft size={24} /> : <Menu size={24} />}
            </button>
            <div className="flex items-center gap-3">
              {logoUrl ? (
                <div className="p-2 rounded-lg shadow-sm" style={{ backgroundColor: colors.card_bg_color }}>
                  <img
                    src={logoUrl}
                    alt={companyName}
                    className="h-8 w-auto object-contain"
                  />
                </div>
              ) : (
                <div className="p-2 rounded-lg" style={{ backgroundColor: colors.primary_color }}>
                  <Droplet size={24} className="text-white" />
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold" style={{ color: colors.text_primary_color }}>{companyName}</h1>
                <p className="text-xs" style={{ color: colors.text_secondary_color }}>{t('header.inspectionManagement')}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium" style={{ color: colors.text_primary_color }}>{userEmail}</p>
              <p className="text-xs" style={{ color: colors.text_secondary_color }}>{userRole || 'User'}</p>
            </div>
            <div className="relative">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors text-sm"
                style={{ backgroundColor: colors.hover_bg_color, color: colors.text_primary_color }}
                title={t('language.switchTo')}
              >
                <Globe size={16} />
                <span className="hidden sm:inline uppercase">{i18n.language.substring(0, 2)}</span>
              </button>
              {isLangMenuOpen && (
                <>
                  <div className="fixed inset-0 z-50" onClick={() => setIsLangMenuOpen(false)} />
                  <div
                    className="absolute right-0 mt-1 w-36 rounded-lg shadow-lg py-1 z-50"
                    style={{ backgroundColor: colors.card_bg_color, border: `1px solid ${colors.border_color}` }}
                  >
                    {supportedLanguages.map((lang) => (
                      <button
                        key={lang}
                        onClick={() => handleLanguageChange(lang)}
                        className="w-full text-left px-4 py-2 text-sm transition-colors"
                        style={{
                          backgroundColor: i18n.language.startsWith(lang) ? colors.active_item_bg : 'transparent',
                          color: i18n.language.startsWith(lang) ? colors.active_item_text : colors.text_primary_color,
                          fontWeight: i18n.language.startsWith(lang) ? 500 : 400,
                        }}
                        onMouseEnter={(e) => {
                          if (!i18n.language.startsWith(lang)) e.currentTarget.style.backgroundColor = colors.hover_bg_color;
                        }}
                        onMouseLeave={(e) => {
                          if (!i18n.language.startsWith(lang)) e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        {t(`language.${lang}`)}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="relative">
              <button
                onClick={() => setIsDesignMenuOpen(!isDesignMenuOpen)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors text-sm"
                style={{ backgroundColor: colors.hover_bg_color, color: colors.text_primary_color }}
                title="Sign-in screen design"
                aria-label="Sign-in screen design preference"
              >
                <ImageIcon size={16} />
              </button>
              {isDesignMenuOpen && (
                <>
                  <div className="fixed inset-0 z-50" onClick={() => setIsDesignMenuOpen(false)} />
                  <div
                    className="absolute right-0 mt-1 w-56 rounded-lg shadow-lg py-1 z-50"
                    style={{ backgroundColor: colors.card_bg_color, border: `1px solid ${colors.border_color}` }}
                  >
                    <div
                      className="px-4 pt-2 pb-1 text-xs uppercase tracking-wide"
                      style={{ color: colors.text_secondary_color }}
                    >
                      Sign-in design
                    </div>
                    {(
                      [
                        { id: 'random', label: 'Random (rotate each visit)' },
                        { id: 'split-panel', label: 'Split-panel corporate' },
                        { id: 'photogenic', label: 'Photogenic split' },
                      ] as { id: SigninDesign; label: string }[]
                    ).map((opt) => {
                      const active = signinDesign === opt.id;
                      return (
                        <button
                          key={opt.id}
                          onClick={() => handleSigninDesignChange(opt.id)}
                          className="w-full text-left px-4 py-2 text-sm transition-colors"
                          style={{
                            backgroundColor: active ? colors.active_item_bg : 'transparent',
                            color: active ? colors.active_item_text : colors.text_primary_color,
                            fontWeight: active ? 500 : 400,
                          }}
                          onMouseEnter={(e) => {
                            if (!active) e.currentTarget.style.backgroundColor = colors.hover_bg_color;
                          }}
                          onMouseLeave={(e) => {
                            if (!active) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <FeedbackNotificationsBell enabled={Boolean(userId)} colors={colors} />
            <ConnectionStatus colors={colors} />
            <ThemeSwitcher
              userRole={userRole}
              userThemeSlug={userThemeSlug}
              onThemeChange={handleThemeChange}
              colors={colors}
            />
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
              style={{ backgroundColor: colors.hover_bg_color, color: colors.text_primary_color }}
            >
              <LogOut size={18} />
              <span className="hidden sm:inline">{t('header.signOut')}</span>
            </button>
          </div>
        </div>
      </header>

      <NotificationBanner />

      <SupportChatModal />

      <div className="flex flex-1 relative min-h-0">
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        <aside
          className={`${
            isSidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full w-0'
          } fixed lg:relative inset-y-0 left-0 top-[73px] lg:top-0 transition-all duration-300 ease-in-out overflow-hidden z-40 flex-shrink-0`}
          style={{
            backgroundColor: colors.menu_bg_color,
            color: menuTextColor,
            borderRight: `1px solid ${colors.border_color}`,
          }}
        >
          <nav className="p-4 space-y-2">
            {(userRole === 'Manager' || userRole === 'Admin') && (
              <button
                onClick={() => {
                  onNavigate?.('dashboard');
                  if (window.innerWidth < 1024) setIsSidebarOpen(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-3 rounded-lg transition-colors font-medium"
                style={{
                  backgroundColor: currentPage === 'dashboard' ? colors.active_item_bg : 'transparent',
                  color: currentPage === 'dashboard' ? colors.active_item_text : menuTextColor,
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== 'dashboard') e.currentTarget.style.backgroundColor = colors.hover_bg_color;
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== 'dashboard') e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <LayoutDashboard size={18} />
                <span>{t('nav.dashboard', 'Dashboard')}</span>
              </button>
            )}
            <div>
              <button
                onClick={() => setIsInspectionsOpen(!isInspectionsOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors font-medium"
                style={{ color: menuTextColor }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.hover_bg_color}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span>{t('nav.inspections')}</span>
                {isInspectionsOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>

              {isInspectionsOpen && (
                <div className="mt-1 ml-4 space-y-1">
                  <NavItem
                    label={t('nav.startInspection')}
                    active={currentPage === 'start-inspection' || currentPage === 'inspection-form'}
                    colors={colors}
                    onClick={() => {
                      onNavigate?.('start-inspection');
                      if (window.innerWidth < 1024) setIsSidebarOpen(false);
                    }}
                  />
                  <NavItem
                    label={t('nav.manageDevices', 'Manage Devices')}
                    active={currentPage === 'manage-devices'}
                    colors={colors}
                    onClick={() => {
                      onNavigate?.('manage-devices');
                      if (window.innerWidth < 1024) setIsSidebarOpen(false);
                    }}
                  />
                  {/* Managers get read-only property detail here. Admins reach
                      the editable version through Settings, so this entry is
                      Manager-only rather than Manager-plus — otherwise an Admin
                      would see Properties listed twice. */}
                  {userRole === 'Manager' && (
                    <NavItem
                      label={t('nav.properties')}
                      active={currentPage === 'job-sites'}
                      colors={colors}
                      onClick={() => {
                        onNavigate?.('job-sites');
                        if (window.innerWidth < 1024) setIsSidebarOpen(false);
                      }}
                    />
                  )}
                  <NavItem
                    label={t('nav.viewInspections')}
                    active={currentPage === 'view-inspections' || currentPage === 'view-report'}
                    colors={colors}
                    onClick={() => {
                      onNavigate?.('view-inspections');
                      if (window.innerWidth < 1024) setIsSidebarOpen(false);
                    }}
                  />
                  {(userRole === 'Manager' || userRole === 'Admin') && (
                    <NavItem
                      label={t('nav.inspectionsDashboard')}
                      active={currentPage === 'inspections-dashboard'}
                      colors={colors}
                      onClick={() => {
                        onNavigate?.('inspections-dashboard');
                        if (window.innerWidth < 1024) setIsSidebarOpen(false);
                      }}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Feedback is reachable by everyone now — a technician sees only
                their own reports, scoped server-side. Admins already reach it
                under Settings, so this top-level entry is for everyone else to
                avoid listing it twice for them. */}
            {userRole !== 'Admin' && (
              <NavItem
                label={t('nav.feedback')}
                active={currentPage === 'feedback-management'}
                colors={colors}
                onClick={() => {
                  onNavigate?.('feedback-management');
                  if (window.innerWidth < 1024) setIsSidebarOpen(false);
                }}
              />
            )}

            {(userRole === 'Manager' || userRole === 'Admin') && (
              <div>
                <button
                  onClick={() => setIsReportsOpen(!isReportsOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors font-medium"
                  style={{ color: menuTextColor }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.hover_bg_color}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div className="flex items-center gap-2">
                    <BarChart3 size={18} />
                    <span>{t('nav.reports', 'Reports')}</span>
                  </div>
                  {isReportsOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </button>

                {isReportsOpen && (
                  <div className="mt-1 ml-4 space-y-1">
                    <NavItem
                      label={t('nav.complianceReport', 'Compliance Report')}
                      active={currentPage === 'compliance-report'}
                      colors={colors}
                      onClick={() => {
                        onNavigate?.('compliance-report');
                        if (window.innerWidth < 1024) setIsSidebarOpen(false);
                      }}
                    />
                    <NavItem
                      label={t('nav.inspectionCompletion', 'Inspection Completion')}
                      active={currentPage === 'inspection-completion'}
                      colors={colors}
                      onClick={() => {
                        onNavigate?.('inspection-completion');
                        if (window.innerWidth < 1024) setIsSidebarOpen(false);
                      }}
                    />
                    <NavItem
                      label={t('nav.wateringSchedules', 'Watering Schedules')}
                      active={currentPage === 'watering-schedules'}
                      colors={colors}
                      onClick={() => {
                        onNavigate?.('watering-schedules');
                        if (window.innerWidth < 1024) setIsSidebarOpen(false);
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {userRole === 'Admin' && (
              <div>
                <button
                  onClick={() => setIsSettingsOpen(!isSettingsOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors font-medium"
                  style={{ color: menuTextColor }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.hover_bg_color}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div className="flex items-center gap-2">
                    <Settings size={18} />
                    <span>{t('nav.settings')}</span>
                  </div>
                  {isSettingsOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </button>

                {isSettingsOpen && (
                  <div className="mt-1 ml-4 space-y-1">
                    {([
                      { page: 'user-management' as const, label: t('nav.userManagement') },
                      { page: 'job-sites' as const, label: t('nav.properties') },
                      { page: 'job-codes' as const, label: t('nav.systemWorkCodes') },
                      { page: 'report-status' as const, label: t('nav.reportStatus') },
                      { page: 'controller-settings' as const, label: t('nav.controllerSettings') },
                      { page: 'water-restrictions' as const, label: t('nav.waterRestrictions', 'Water Restrictions') },
                      { page: 'theme-management' as const, label: t('nav.themes') },
                      { page: 'notifications' as const, label: t('nav.notifications', 'Notifications') },
                      { page: 'integrations' as const, label: t('nav.integrations') },
                      { page: 'feedback-management' as const, label: t('nav.feedback') },
                      { page: 'audit-log' as const, label: t('nav.activityLog') },
                      { page: 'event-log' as const, label: t('nav.eventLog', 'Event Log') },
                      { page: 'gps-diagnostics' as const, label: t('nav.gpsDiagnostics', 'GPS Diagnostics') },
                    ]).map((item) => (
                      <NavItem
                        key={item.page}
                        label={item.label}
                        active={currentPage === item.page}
                        colors={colors}
                        onClick={() => {
                          onNavigate?.(item.page);
                          if (window.innerWidth < 1024) setIsSidebarOpen(false);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>
        </aside>

        <main className="flex-1 min-h-0 overflow-auto p-6">
          {children}
        </main>
      </div>

      <footer
        className="py-3 px-6 flex-shrink-0"
        style={{
          backgroundColor: colors.footer_bg_color,
          color: colors.footer_text_color,
          borderTop: `1px solid ${colors.border_color}`,
        }}
      >
        <div className="flex items-center justify-between text-sm">
          <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          <div className="flex items-center gap-3">
            <button
              onClick={onOpenFeedback}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-full transition-colors shadow-sm"
              style={{ backgroundColor: colors.primary_color }}
              title={t('feedback.tooltip')}
            >
              <MessageSquarePlus size={14} />
              <span className="hidden sm:inline">{t('feedback.button')}</span>
            </button>
            <button
              onClick={() => setIsChangelogOpen(true)}
              className="hover:underline transition-opacity hover:opacity-80 cursor-pointer"
              title={`Build ${BUILD_SHORT_SHA}${BUILD_AT ? ` (${BUILD_AT})` : ''}`}
            >
              {changelog[0]?.version ?? t('footer.version')}
              <span className="ml-1.5 text-xs opacity-60">·&nbsp;{BUILD_SHORT_SHA}</span>
            </button>
          </div>
        </div>
      </footer>

      {isChangelogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div
            className="rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col"
            style={{ backgroundColor: colors.card_bg_color }}
          >
            <div
              className="flex items-center justify-between px-6 py-4"
              style={{ borderBottom: `1px solid ${colors.border_color}` }}
            >
              <div>
                <h2 className="text-lg font-semibold" style={{ color: colors.text_primary_color }}>Changelog</h2>
                <p className="text-xs mt-0.5" style={{ color: colors.text_secondary_color }}>
                  Running build <span className="font-mono">{BUILD_SHORT_SHA}</span>
                  {BUILD_AT ? ` · built ${new Date(BUILD_AT).toLocaleString()}` : ''}
                </p>
              </div>
              <button
                onClick={() => setIsChangelogOpen(false)}
                className="p-1.5 rounded-lg transition-colors"
                style={{ color: colors.text_secondary_color }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.hover_bg_color}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto px-6 py-4 space-y-6">
              {changelog.map((entry) => (
                <div key={entry.version}>
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="text-base font-semibold" style={{ color: colors.text_primary_color }}>{entry.version}</span>
                    <span className="text-sm" style={{ color: colors.text_secondary_color }}>{entry.date}</span>
                  </div>
                  <ul className="space-y-1.5">
                    {entry.changes.map((change, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm" style={{ color: colors.text_primary_color }}>
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors.primary_color }} />
                        {change}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NavItem({
  label,
  active,
  colors,
  onClick,
}: {
  label: string;
  active: boolean;
  colors: { active_item_bg: string; active_item_text: string; menu_text_color: string; hover_bg_color: string };
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-4 py-2 rounded-lg transition-colors text-sm"
      style={{
        backgroundColor: active ? colors.active_item_bg : 'transparent',
        color: active ? colors.active_item_text : colors.menu_text_color,
        fontWeight: active ? 600 : 400,
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.backgroundColor = colors.hover_bg_color;
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      {label}
    </button>
  );
}
